import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
  }
}

const baseColors = {
  primary: {
    main: '#60A442',
    light: '#7AB462',
    dark: '#3F7A2A',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#3F7A2A',
    light: '#7AB462',
    dark: '#2F5A20',
    contrastText: '#ffffff',
  },
  success: { main: '#059669', light: '#10B981', dark: '#047857' },
  warning: { main: '#D97706', light: '#F59E0B', dark: '#B45309' },
  error: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C' },
  info: { main: '#4C8534', light: '#60A442', dark: '#2F5A20' },
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...baseColors,
    background: {
      default: '#F6F8F4',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#3F4753',
    },
    divider: '#E8ECE6',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.3 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)',
    '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)',
    '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -2px rgba(0,0,0,0.05)',
    '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 10px 10px -5px rgba(0,0,0,0.04)',
    '0px 25px 50px -12px rgba(0,0,0,0.25)',
    ...Array(20).fill('none'),
  ] as any,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          padding: '8px 20px',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 12px rgba(96,164,66, 0.2)' },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #60A442 0%, #7AB462 100%)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.06)',
          border: '1px solid #E8ECE6',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': { borderColor: '#E5E9E3' },
            '&:hover fieldset': { borderColor: '#60A442' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 600, fontSize: '0.75rem' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#F4F8F1',
          fontWeight: 600,
          color: '#5C6B52',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E8ECE6',
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 1px 0 #E8ECE6',
        },
      },
    },
    // Dropdowns & popups app-wide (Select menus, context menus, popovers, autocomplete lists) get a
    // clear outline + drop shadow so they never blend into the page behind them (16 Oct 2026, user).
    // Border uses palette.divider so it is correct in light AND dark; the dark theme inherits these
    // via its `...lightTheme.components` spread, and the callback runs against the active theme.
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          border: '1px solid',
          borderColor: theme.palette.divider,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          border: '1px solid',
          borderColor: theme.palette.divider,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          border: '1px solid',
          borderColor: theme.palette.divider,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        }),
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...baseColors,
    background: {
      default: '#111827',
      paper: '#1E293B',
    },
    text: {
      primary: '#F4F8F1',
      secondary: '#8B929B',
    },
    divider: '#334155',
  },
  typography: lightTheme.typography,
  shape: lightTheme.shape,
  components: {
    ...lightTheme.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(51, 65, 85, 0.8)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          background: '#1E293B',
        },
      },
    },
  },
});
